// Referrals, Offers (docs/referrals-spec.md 4.6): 1 card per paid product (not the 1-2-1), in the boards'
// order: shown or hidden, its terms, fee and link in 1 line, and Edit and Hide or Show. A product with no
// offer has only Add an offer. Every save is hub.save_referral_offer (migration 79), which checks the link,
// the fee and the wording again and refuses with its own codes.
//
// The editor warns as Adam types when a text breaks a writing rule (a dash, an exclamation mark, curly
// quotes, "deposit", "room"), and still lets him save (the spec's default 6).
import { useState } from 'react'
import { Button, Card, Checkbox, Dialog, Field, Loading, Problem, Select, StatusPill, TextArea, TextInput, useAction, useAdmin } from '../ui/index.js'
import { REF, offerBody, offerErrors, offerForm, offerSummary, refusal, writingWarnings } from './words.js'
import { offerProducts } from './data.js'

function Warnings({ text }) {
  const w = writingWarnings(text)
  if (!w.length) return null
  return <span className="rf-warn" role="note">{w.join(' ')}</span>
}

function OfferEditor({ product, offer, onClose, onSaved }) {
  const { api } = useAdmin()
  const [f, setF] = useState(() => offerForm(offer))
  const [errors, setErrors] = useState({})
  const save = useAction(body => api.save_referral_offer({ product: product.id, offer: body }))
  const set = key => value => setF(x => ({ ...x, [key]: value }))
  const stripe = f.ref_param === 'client_reference_id'

  async function go() {
    const found = offerErrors(f)
    setErrors(found)
    if (Object.keys(found).length) return
    const answer = await save.run(offerBody(f))
    if (answer) onSaved(REF.saved)
  }

  const text = (key, label, { area = false, rows = 3, max } = {}) => (
    <Field label={label} plain error={errors[key]}>
      {props => (
        <>
          {area
            ? <TextArea {...props} rows={rows} value={f[key]} onChange={set(key)} maxLength={max} />
            : <TextInput {...props} value={f[key]} onChange={set(key)} maxLength={max} autoComplete="off" />}
          <Warnings text={f[key]} />
        </>
      )}
    </Field>
  )

  return (
    <Dialog eyebrow={REF.offerEyebrow} title={product.name} onClose={onClose} busy={save.busy} wide
      footer={<>
        <Button onClick={onClose} disabled={save.busy}>{REF.buttons.cancel}</Button>
        <Button kind="ink" busy={save.busy} onClick={go}>{REF.saveOffer}</Button>
      </>}>
      <div className="ad-form rf-offer-form">
        <Field label={REF.fields.link} plain error={errors.intake_link}>
          {props => <TextInput {...props} type="url" value={f.intake_link} onChange={set('intake_link')} maxLength={500} autoComplete="off" />}
        </Field>
        <div className="ad-row">
          <Field label={REF.fields.param} plain>
            {props => <Select {...props} value={f.ref_param} onChange={set('ref_param')} options={REF.paramOptions} />}
          </Field>
          {stripe && (
            <Field label={REF.fields.startsAs} plain error={errors.starts_as}>
              {props => <Select {...props} value={f.starts_as} onChange={set('starts_as')} options={REF.startsOptions} />}
            </Field>
          )}
        </div>
        <div className="ad-row rf-row-3">
          <Field label={REF.fields.fee} plain>
            {props => <Select {...props} value={f.fee_kind} onChange={set('fee_kind')} options={REF.feeOptions} />}
          </Field>
          <Field label={f.fee_kind === 'percentage' ? REF.fields.percent : REF.fields.amount} plain error={errors.fee_value}>
            {props => <TextInput {...props} inputMode="decimal" value={f.fee_value} onChange={set('fee_value')} maxLength={12} />}
          </Field>
          <Field label={REF.fields.monthly} plain error={errors.price_monthly}>
            {props => <TextInput {...props} inputMode="decimal" value={f.price_monthly} onChange={set('price_monthly')} maxLength={12} />}
          </Field>
        </div>
        {text('price_terms', REF.fields.priceTerms, { max: 200 })}
        {text('qualify_rule', REF.fields.qualifyRule, { max: 300 })}
        {text('positioning_line', REF.fields.positioning, { max: 300 })}
        {text('who_its_for', REF.fields.whoFor, { area: true, max: 1000 })}
        {text('key_points', REF.fields.keyPoints, { area: true, rows: 5 })}
        {text('how_to_introduce', REF.fields.howTo, { area: true, max: 1000 })}
        {text('never_say', REF.fields.neverSay, { area: true, rows: 3 })}
        <Checkbox checked={f.is_active} onChange={set('is_active')}>{REF.fields.active}</Checkbox>
        {save.error && <p className="ad-error" role="alert">{refusal(save.error)}</p>}
      </div>
    </Dialog>
  )
}

export function Offers({ offers, products }) {
  const { api, setFlash } = useAdmin()
  const [editing, setEditing] = useState(null)
  const [busy, setBusy] = useState(null)
  const list = offerProducts(products)
  const by = new Map((offers.data || []).map(o => [o.product_id, o]))

  async function toggle(p, o) {
    if (busy) return
    setBusy(p.id)
    try {
      await api.save_referral_offer({ product: p.id, offer: { is_active: !o.is_active } })
      setFlash({ text: o.is_active ? REF.hiddenFlash : REF.shownFlash })
      offers.reload()
    } catch (e) {
      setFlash({ text: refusal(e), tone: 'problem' })
    } finally { setBusy(null) }
  }

  if (offers.status === 'loading' && !offers.data) return <Loading />
  if (!offers.data) return <Problem error={offers.error} onRetry={offers.reload} />
  return (
    <>
      <div className="rf-offers">
        {list.map(p => {
          const o = by.get(p.id)
          return (
            <Card key={p.id} title={p.name} id={`rf-offer-${p.id}`} className="rf-offer"
              aside={o ? <StatusPill tone={o.is_active ? 'active' : 'ended'}>{o.is_active ? REF.shown : REF.hidden}</StatusPill> : undefined}>
              {o ? (
                <>
                  <p className="rf-offer-line">{offerSummary(o)}</p>
                  {!o.intake_link && <p className="rf-offer-note">{REF.noLink}</p>}
                  <span className="rf-offer-acts">
                    <Button size="sm" kind="ink" onClick={() => setEditing(p)}>{REF.edit}</Button>
                    <Button size="sm" busy={busy === p.id} onClick={() => toggle(p, o)}>{o.is_active ? REF.hide : REF.show}</Button>
                  </span>
                </>
              ) : (
                <span className="rf-offer-acts"><Button size="sm" onClick={() => setEditing(p)}>{REF.addOffer}</Button></span>
              )}
            </Card>
          )
        })}
      </div>
      {editing && (
        <OfferEditor product={editing} offer={by.get(editing.id) || null} onClose={() => setEditing(null)}
          onSaved={text => { setEditing(null); setFlash({ text }); offers.reload() }} />
      )}
    </>
  )
}
